import "./index.scss"

import React, {memo, useCallback, useMemo, useState, MouseEvent} from "react"
import {PasswordContainer} from "../../../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../../../components/Settings/SettingsPageContainer"
import {ColumnModel, SettingsList} from "../../../../../../components/Settings/SettingsList"
import {useSelector} from "react-redux"
import {HomepageItemModel, HomepageItemType} from "../../../../../../store/control/state"
import {useRPC} from "../../../../../../hooks/rpc"
import {useSetSelector} from "../../../../../../hooks/set-selector"
import {useLocalization} from "../../../../../../hooks/localization"
import {useQuestion} from "../../../../../../hooks/question"
import {UpDown} from "../../components/UpDown";
import {useDispatch} from 'react-redux';
import {changeBusyAction} from '../../../../../../store/ui/actions';
import { hasAuthority } from "../../../../../../utils/role"

export const TYPE_NAME_MAP: Record<HomepageItemType, string> = {
    control: "CONTROL",
    scenario: "SCENARIO",
    sensor: "SENSOR",
    media: "MEDIA"
}
interface Button {
    text: string;
    className: string;
    onClick?: () => void; // Make onClick optional
    url?: string | null; // Make url optional and allow null as a value
    hidden?: boolean | false
}

const keyExtractor = (item: HomepageItemModel) => item.id

export const HomepageListScreen = memo(function HomepageListScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const question = useQuestion()
    const dispatch = useDispatch()

    const homepageItemModelList = useSelector(state => state.control.homepageItemList)
    const homepageItemModelListSorted = useMemo(
        () => homepageItemModelList.sort((a, b) => a.order - b.order),
        [homepageItemModelList]
    )
    const controlSet = useSetSelector(state => state.control.controlList)
    const scenarioSet = useSetSelector(state => state.control.scenarioList)
    const inputSet = useSetSelector(state => state.control.inputList)
    const mediaSet = useSetSelector(state => state.control.mediaList)

    const [selectedHomepageItem, setSelectedHomepageItem] = useState<HomepageItemModel>(null)
    const role = useSelector(state => state.ui.role);

    const onClickAutoSorting = useCallback(() => {
        return question(t("are_you_sure"), () => {
            return rpc.updateChangeOrder({is_auto_sorting: true, type: "homepage"})
        })
    }, [t, question, rpc])

    const buttons = useMemo(
        () => {
          let _buttons: Button[] = [{
            text: "A-Z (Auto)",
            onClick: onClickAutoSorting,
            className: "blue"
          }];
          if (hasAuthority(role, "service")) {
            _buttons = _buttons.concat([
              {
                  text: t("edit"),
                  url: `/settings/custom-menu/sorting-menu/homepage/edit/${selectedHomepageItem ? selectedHomepageItem.id : ""}`,
                  className: "orange",
                  hidden: !selectedHomepageItem
              },
              {
                  text: t("add"),
                  url: "/settings/custom-menu/sorting-menu/homepage/edit",
                  className: "green"
              }
            ])
          }
          return _buttons
        }, [t, onClickAutoSorting, selectedHomepageItem, role]
    )

    const swapItemOrders = useCallback(
        (itemA: HomepageItemModel, itemB: HomepageItemModel) => {
            dispatch(changeBusyAction(true))
            Promise.all([
                rpc.updateHomepageItem({...itemA, order: itemB.order}),
                rpc.updateHomepageItem({...itemB, order: itemA.order})
            ]).then(() => {
              setSelectedHomepageItem(null);
              dispatch(changeBusyAction(false))
            })
        },
        [rpc]
    )

    const onUpClick = useCallback(
        (index: number) => {
            swapItemOrders(homepageItemModelList[index - 1], homepageItemModelList[index])
        },
        [homepageItemModelList, swapItemOrders]
    )

    const onDownClick = useCallback(
        (index: number) => {
            swapItemOrders(homepageItemModelList[index + 1], homepageItemModelList[index])
        },
        [homepageItemModelList, swapItemOrders]
    )

    const displayNameExtractor = useCallback(
        (item: HomepageItemModel, column: ColumnModel, index?: number) => {
            if (column.key === "name") {
                switch (item.type) {
                    case "control":
                        return controlSet[item.relational_id]?.name || "-"
                    case "sensor":
                        return inputSet[item.relational_id]?.name || "-"
                    case "scenario":
                        return scenarioSet[item.relational_id]?.name || "-"
                    case "media":
                        return mediaSet[item.relational_id]?.name || "-"
                    default:
                        return "-"
                }
            } else if (column.key === "type") {
                return t(TYPE_NAME_MAP[item.type]) || "-"
            } else if (column.key === "order") {
                return (
                    <UpDown
                        index={index}
                        onUpClick={index === 0 ? undefined : onUpClick}
                        onDownClick={index === homepageItemModelListSorted.length - 1 ? undefined : onDownClick}
                    />
                )
            }

            return item[column.key] || "-"
        },
        [t, controlSet, inputSet, scenarioSet, mediaSet, onUpClick, homepageItemModelListSorted.length, onDownClick]
    )

    const columns = useMemo<ColumnModel[]>(
        () => [
            {
                length: 3,
                name: t("name"),
                key: "name"
            },
            {
                length: 2,
                name: t("type"),
                key: "type"
            },
            {
                length: 3,
                name: t("order"),
                key: "order"
            }
        ],
        [t]
    )

    return (
        <>
            <PasswordContainer requiredRole="admin" isFullscreen={true}>
                <SettingsPageContainer buttons={buttons} backUrl={"/settings/custom-menu"}>
                    <SettingsList
                        sortDisabled={true}
                        columns={columns}
                        items={homepageItemModelListSorted}
                        displayNameExtractor={displayNameExtractor}
                        keyExtractor={keyExtractor}
                        selectedItem={selectedHomepageItem}
                        onItemSelected={setSelectedHomepageItem}
                    />
                </SettingsPageContainer>
            </PasswordContainer>
        </>
    )
})
